import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Search, Trash2, Edit, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VAsManagement() {
  const { user } = useAuth();
  const [vas, setVas] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [openCreate, setOpenCreate] = useState(false);
  const [newVA, setNewVA] = useState({ name: '', email: '', role: '' });
  const [creatingVA, setCreatingVA] = useState(false);
  const [openUserCreate, setOpenUserCreate] = useState(false);
  const [selectedVAForUser, setSelectedVAForUser] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingVA, setEditingVA] = useState(null);
  const [editPassword, setEditPassword] = useState('');
  const [updatingVA, setUpdatingVA] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch VAs and available roles (filtered by permissions on backend)
      const [vasRes, rolesRes] = await Promise.all([
        axios.get(`${API_URL}/vas`),
        axios.get(`${API_URL}/vas/roles/list`)
      ]);
      setVas(vasRes.data);
      setAvailableRoles(rolesRes.data.roles || []);
      
      // Set default role for new VA
      if (rolesRes.data.roles?.length > 0) {
        setNewVA(prev => ({ ...prev, role: rolesRes.data.roles[0] }));
      }
      
      // Only fetch users if admin (used for checking existing accounts)
      if (user?.role === 'admin') {
        try {
          const usersRes = await axios.get(`${API_URL}/users`);
          setUsers(usersRes.data || []);
        } catch {
          setUsers([]);
        }
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVA = async (e) => {
    e.preventDefault();
    setCreatingVA(true);
    
    try {
      await axios.post(`${API_URL}/vas`, newVA);
      toast.success('VA created successfully');
      setOpenCreate(false);
      setNewVA({ name: '', email: '', role: availableRoles[0] || '' });
      fetchData();
    } catch (error) {
      console.error('Error creating VA:', error);
      toast.error(error.response?.data?.detail || 'Failed to create VA');
    } finally {
      setCreatingVA(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!selectedVAForUser) {
      toast.error('Please select a VA');
      return;
    }
    
    setCreatingUser(true);
    
    try {
      const selectedVA = vas.find(va => va.va_id === selectedVAForUser);
      await axios.post(`${API_URL}/auth/register`, {
        name: selectedVA.name,
        email: selectedVA.email,
        password: newUserPassword,
        role: 'va'
      });
      toast.success('User account created successfully');
      setOpenUserCreate(false);
      setSelectedVAForUser('');
      setNewUserPassword('');
      fetchData();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user account');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteVA = async (vaId) => {
    if (!window.confirm('Are you sure you want to delete this VA?')) return;
    
    console.log('Attempting to delete VA with ID:', vaId);
    
    try {
      const url = `${API_URL}/vas/${vaId}`;
      console.log('DELETE URL:', url);
      
      const response = await axios.delete(url);
      console.log('Delete response:', response);
      
      toast.success('VA deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting VA:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      toast.error(error.response?.data?.detail || 'Failed to delete VA');
    }
  };

  const handleOpenEdit = (va) => {
    setEditingVA({ ...va });
    setEditPassword('');
    setOpenEdit(true);
  };

  const handleUpdateVA = async (e) => {
    e.preventDefault();
    setUpdatingVA(true);
    
    try {
      const updateData = {
        name: editingVA.name,
        email: editingVA.email,
        role: editingVA.role,
        wallet_address: editingVA.wallet_address || null
      };
      
      // Only include password if a new one was entered
      if (editPassword && editPassword.trim() !== '') {
        updateData.password = editPassword;
      }
      
      await axios.put(`${API_URL}/vas/${editingVA.va_id}`, updateData);
      toast.success('VA updated successfully');
      setOpenEdit(false);
      setEditingVA(null);
      setEditPassword('');
      fetchData();
    } catch (error) {
      console.error('Error updating VA:', error);
      toast.error(error.response?.data?.detail || 'Failed to update VA');
    } finally {
      setUpdatingVA(false);
    }
  };

  const hasUserAccount = (va) => {
    // Use has_account from backend if available, otherwise check users list
    if (va.has_account !== undefined) {
      return va.has_account;
    }
    return users.some(user => user.email === va.email && user.role === 'va');
  };

  const vasWithoutAccount = vas.filter(va => !hasUserAccount(va));

  const filteredVAs = vas.filter(
    (va) =>
      va.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      va.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="vas-title">
            Virtual Assistants
          </h1>
          <p className="text-base text-muted-foreground mt-2">Manage your VA team</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openUserCreate} onOpenChange={setOpenUserCreate}>
            <DialogTrigger asChild>
              <Button className="bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700" data-testid="create-user-button">
                <UserPlus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle className="text-white">Create User Account</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Create a new user account for a VA to access the platform
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="select-va" className="text-white">Select VA</Label>
                  {vasWithoutAccount.length === 0 ? (
                    <div className="text-sm text-zinc-400 p-3 bg-zinc-900/50 rounded-md">
                      All VAs already have user accounts
                    </div>
                  ) : (
                    <select
                      id="select-va"
                      value={selectedVAForUser}
                      onChange={(e) => setSelectedVAForUser(e.target.value)}
                      required
                      className="w-full bg-zinc-900/50 border border-zinc-800 text-white rounded-md px-3 py-2 focus:outline-none focus:border-white/50"
                      data-testid="select-va-for-user"
                    >
                      <option value="">Select a VA without account</option>
                      {vasWithoutAccount.map((va) => (
                        <option key={va.va_id} value={va.va_id}>
                          {va.name} ({va.email})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                {selectedVAForUser && (
                  <div className="text-xs text-zinc-400 p-2 bg-zinc-900/50 rounded-md">
                    ℹ️ Account will be created for: <span className="text-white">{vas.find(v => v.va_id === selectedVAForUser)?.name}</span>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="user-password" className="text-white">Password</Label>
                  <Input
                    id="user-password"
                    type="password"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    required
                    className="bg-zinc-900/50 border-zinc-800 text-white"
                    placeholder="Enter password"
                    data-testid="user-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-white text-black hover:bg-zinc-200"
                  disabled={creatingUser || vasWithoutAccount.length === 0}
                  data-testid="user-create-submit-button"
                >
                  {creatingUser ? 'Creating...' : 'Create User'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button className="bg-white text-black hover:bg-zinc-200" data-testid="create-va-button">
                <Plus className="w-4 h-4 mr-2" />
                Add VA
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle className="text-white">Add Virtual Assistant</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Add a new VA profile to track performance
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateVA} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="va-name" className="text-white">Name</Label>
                  <Input
                    id="va-name"
                    value={newVA.name}
                    onChange={(e) => setNewVA({ ...newVA, name: e.target.value })}
                    required
                    className="bg-zinc-900/50 border-zinc-800 text-white"
                    data-testid="va-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="va-email" className="text-white">Email</Label>
                  <Input
                    id="va-email"
                    type="email"
                    value={newVA.email}
                    onChange={(e) => setNewVA({ ...newVA, email: e.target.value })}
                    required
                    className="bg-zinc-900/50 border-zinc-800 text-white"
                    data-testid="va-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="va-role" className="text-white">Role</Label>
                  <select
                    id="va-role"
                    value={newVA.role}
                    onChange={(e) => setNewVA({ ...newVA, role: e.target.value })}
                    required
                    className="w-full bg-zinc-900/50 border border-zinc-800 text-white rounded-md px-3 py-2 focus:outline-none focus:border-white/50"
                    data-testid="va-role-select"
                  >
                    {availableRoles.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-white text-black hover:bg-zinc-200"
                  disabled={creatingVA}
                  data-testid="va-create-submit-button"
                >
                  {creatingVA ? 'Creating...' : 'Add VA'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
        <Input
          placeholder="Search VAs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500"
          data-testid="search-vas-input"
        />
      </div>

      <div className="bg-card rounded-md border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Account</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredVAs.map((va) => (
              <TableRow
                key={va.va_id}
                className="border-b border-border table-row-hover"
                data-testid={`va-row-${va.va_id}`}
              >
                <TableCell 
                  className="font-medium text-white cursor-pointer"
                  onClick={() => navigate(`/vas/${va.va_id}`)}
                >
                  {va.name}
                </TableCell>
                <TableCell 
                  className="text-zinc-400 cursor-pointer text-sm"
                  onClick={() => navigate(`/vas/${va.va_id}`)}
                >
                  {va.role || 'Threads VA'}
                </TableCell>
                <TableCell 
                  className="text-zinc-300 cursor-pointer"
                  onClick={() => navigate(`/vas/${va.va_id}`)}
                >
                  {va.email}
                </TableCell>
                <TableCell
                  className="cursor-pointer"
                  onClick={() => navigate(`/vas/${va.va_id}`)}
                >
                  {hasUserAccount(va) ? (
                    <span className="bg-green-500/10 text-green-500 border border-green-500/20 px-2 py-0.5 rounded text-xs font-medium">
                      ✓ Active
                    </span>
                  ) : (
                    <span className="bg-zinc-800 text-zinc-400 border border-zinc-700 px-2 py-0.5 rounded text-xs font-medium">
                      No Account
                    </span>
                  )}
                </TableCell>
                <TableCell
                  className="cursor-pointer"
                  onClick={() => navigate(`/vas/${va.va_id}`)}
                >
                  <span className={`bg-green-500/10 text-green-500 border border-green-500/20 px-2 py-0.5 rounded text-xs font-medium`}>
                    {va.status}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleOpenEdit(va);
                      }}
                      className="text-blue-500 hover:text-blue-400 hover:bg-blue-900/20"
                      data-testid={`edit-va-${va.va_id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteVA(va.va_id);
                      }}
                      className="text-red-500 hover:text-red-400 hover:bg-red-900/20"
                      data-testid={`delete-va-${va.va_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit VA Dialog */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Virtual Assistant</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Update VA information and account credentials
            </DialogDescription>
          </DialogHeader>
          {editingVA && (
            <form onSubmit={handleUpdateVA} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-va-name" className="text-white">Name</Label>
                <Input
                  id="edit-va-name"
                  value={editingVA.name}
                  onChange={(e) => setEditingVA({ ...editingVA, name: e.target.value })}
                  required
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  data-testid="edit-va-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-va-email" className="text-white">Email</Label>
                <Input
                  id="edit-va-email"
                  type="email"
                  value={editingVA.email}
                  onChange={(e) => setEditingVA({ ...editingVA, email: e.target.value })}
                  required
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  data-testid="edit-va-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-va-role" className="text-white">Role</Label>
                <select
                  id="edit-va-role"
                  value={editingVA.role || 'Threads VA'}
                  onChange={(e) => setEditingVA({ ...editingVA, role: e.target.value })}
                  required
                  className="w-full bg-zinc-900/50 border border-zinc-800 text-white rounded-md px-3 py-2 focus:outline-none focus:border-white/50"
                  data-testid="edit-va-role-select"
                >
                  {availableRoles.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-va-password" className="text-white">
                  New Password <span className="text-zinc-500 font-normal">(optional)</span>
                </Label>
                <Input
                  id="edit-va-password"
                  type="password"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  placeholder="Leave empty to keep current password"
                  data-testid="edit-va-password-input"
                />
                {hasUserAccount(editingVA) ? (
                  <p className="text-xs text-zinc-500">
                    This VA has an active account. Enter a new password to change it.
                  </p>
                ) : (
                  <p className="text-xs text-amber-500">
                    This VA has no account yet. Create one first to set a password.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-va-wallet" className="text-white">
                  Wallet Address <span className="text-zinc-500 font-normal">(Solana)</span>
                </Label>
                <Input
                  id="edit-va-wallet"
                  value={editingVA.wallet_address || ''}
                  onChange={(e) => setEditingVA({ ...editingVA, wallet_address: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800 text-white font-mono text-sm"
                  placeholder="Solana wallet address"
                  data-testid="edit-va-wallet-input"
                />
                <p className="text-xs text-zinc-500">
                  Required for USDC payments. VA should provide their Solana wallet address.
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-white text-black hover:bg-zinc-200"
                disabled={updatingVA}
                data-testid="va-update-submit-button"
              >
                {updatingVA ? 'Updating...' : 'Update VA'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}